package co.maytes.standupbot.dashboard.features.templates

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.Card
import co.maytes.standupbot.dashboard.components.widgets.SegmentedControl
import co.maytes.standupbot.dashboard.components.widgets.SegmentedOption
import co.maytes.standupbot.dashboard.components.widgets.SlackMessagePreview
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.template.PromptComposer
import co.maytes.standupbot.shared.template.SectionsConfigDto
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px

private enum class PreviewTab(val label: String) { Post("Slack post"), System("System"), User("User") }

private val previewTabOptions = PreviewTab.entries.map { SegmentedOption(it, it.label) }

@Composable
fun TemplatePreviewPane(
    promptBody: String,
    sectionsConfig: SectionsConfigDto,
    workspaceName: String? = null,
) {
    val composed = PromptComposer.compose(
        promptBody,
        PromptFixture.bundle(workspaceName ?: "Your workspace"),
        sectionsConfig,
        PromptFixture.workWindow,
        PromptFixture.eventsBlock,
    )
    var tab by remember { mutableStateOf(PreviewTab.Post) }
    Card(modifier = Modifier.fillMaxWidth()) {
        Column(modifier = Modifier.fillMaxWidth().padding(Spacing.lg).gap(Spacing.md)) {
            Column(modifier = Modifier.fillMaxWidth().gap(2.px)) {
                SpanText(
                    text = "Live preview",
                    modifier = Modifier
                        .fontSize(Typography.h3.size)
                        .fontWeight(Typography.h3.weight)
                        .color(Palette.textPrimary),
                )
                SpanText(
                    text = when (tab) {
                        PreviewTab.Post -> "Sample data. Sections and headings here apply to your posted standup."
                        PreviewTab.System, PreviewTab.User ->
                            "Preview only: the prompt text isn't sent to the AI yet. Shown with sample data."
                    },
                    modifier = Modifier
                        .fontSize(Typography.bodySmall.size)
                        .color(Palette.textSecondary),
                )
            }

            SegmentedControl(
                options = previewTabOptions,
                selected = tab,
                onSelect = { tab = it },
                ariaLabel = "Prompt preview",
            )

            when (tab) {
                PreviewTab.Post -> SlackMessagePreview(layout = sectionsConfig, modifier = Modifier.fillMaxWidth())
                PreviewTab.System -> CodeBlock(composed.systemPrompt)
                PreviewTab.User -> CodeBlock(composed.userMessage)
            }
        }
    }
}

@Composable
private fun CodeBlock(body: String) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("background-color", "#0F0F12")
                property("color", "#E4E4E7")
                property("border-radius", "${Radius.md}")
                property("padding", "${Spacing.md}")
                property("font-family", Typography.FONT_MONO)
                property("font-size", "12.5px")
                property("line-height", "1.6")
                property("white-space", "pre-wrap")
                property("word-break", "break-word")
                property("max-height", "560px")
                property("overflow-y", "auto")
                property("border", "1px solid rgba(255,255,255,0.04)")
            },
    ) {
        SpanText(text = body.ifBlank { "(empty)" })
    }
}
